/* eslint arrow-body-style: 0 */
import { frontendURL } from '../../../helper/URLHelper';
import store from '../../../store';
import ConversationView from './ConversationView.vue';
import { CHAT_QUEUES } from './chatQueues';

const CONVERSATION_PERMISSIONS = [
  'administrator',
  'agent',
  'conversation_manage',
  'conversation_unassigned_manage',
  'conversation_participating_manage',
];

const isFolderAvailable = async folderId => {
  let folders = store.getters['customViews/getConversationCustomViews'];
  if (!folders.length) {
    await store.dispatch('customViews/get', 'conversation');
    folders = store.getters['customViews/getConversationCustomViews'];
  }
  return folders.some(folder => folder.id === Number(folderId));
};

const redirectFolderListIfUnavailable = async (to, _from, next) => {
  if (await isFolderAvailable(to.params.id)) {
    next();
    return;
  }
  next({ name: 'home', params: { accountId: to.params.accountId } });
};

const redirectFolderConversationIfUnavailable = async (to, _from, next) => {
  if (await isFolderAvailable(to.params.id)) {
    next();
    return;
  }
  next({
    name: 'inbox_conversation',
    params: {
      accountId: to.params.accountId,
      conversation_id: to.params.conversation_id,
    },
  });
};

export default {
  routes: [
    {
      path: frontendURL('accounts/:accountId/dashboard'),
      name: 'home',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: () => {
        return { inboxId: 0 };
      },
    },
    {
      path: frontendURL('accounts/:accountId/conversations/:conversation_id'),
      name: 'inbox_conversation',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: route => {
        return { inboxId: 0, conversationId: route.params.conversation_id };
      },
    },
    {
      path: frontendURL('accounts/:accountId/inbox/:inbox_id'),
      name: 'inbox_dashboard',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: route => {
        return { inboxId: route.params.inbox_id };
      },
    },
    {
      path: frontendURL(
        'accounts/:accountId/inbox/:inbox_id/conversations/:conversation_id'
      ),
      name: 'conversation_through_inbox',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: route => {
        return {
          conversationId: route.params.conversation_id,
          inboxId: route.params.inbox_id,
        };
      },
    },
    // The label-scoped screens were removed with the Inbox redesign (labels
    // scope the list through filters/views instead), but their URLs live on in
    // bookmarks: the list page lands on home, an open conversation keeps its
    // thread.
    {
      path: frontendURL('accounts/:accountId/label/:label'),
      redirect: to => ({
        name: 'home',
        params: { accountId: to.params.accountId },
      }),
    },
    {
      path: frontendURL(
        'accounts/:accountId/label/:label/conversations/:conversation_id'
      ),
      redirect: to => ({
        name: 'inbox_conversation',
        params: {
          accountId: to.params.accountId,
          conversation_id: to.params.conversation_id,
        },
      }),
    },
    // Team inboxes: the same list scoped to one team, a section of its own in
    // the Inbox sidebar. Real routes (not filters) so the open conversation
    // stays inside the team's path and the sidebar entry stays lit above it.
    {
      path: frontendURL('accounts/:accountId/team/:teamId'),
      name: 'team_conversations',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: route => ({ teamId: route.params.teamId }),
    },
    {
      path: frontendURL(
        'accounts/:accountId/team/:teamId/conversations/:conversationId'
      ),
      name: 'conversations_through_team',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: route => ({
        conversationId: route.params.conversationId,
        teamId: route.params.teamId,
      }),
    },
    {
      path: frontendURL('accounts/:accountId/custom_view/:id'),
      name: 'folder_conversations',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      beforeEnter: redirectFolderListIfUnavailable,
      component: ConversationView,
      props: route => ({ foldersId: route.params.id }),
    },
    {
      path: frontendURL(
        'accounts/:accountId/custom_view/:id/conversations/:conversation_id'
      ),
      name: 'conversations_through_folders',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      beforeEnter: redirectFolderConversationIfUnavailable,
      props: route => ({
        conversationId: route.params.conversation_id,
        foldersId: route.params.id,
      }),
    },
    // The Chats queues. Each is the same list opened on a pinned scope, so
    // they share ConversationView and differ only in the props they pass -
    // real routes rather than query params so the open conversation stays
    // inside the queue and the sidebar entry stays lit above it.
    ...CHAT_QUEUES.flatMap(({ slug, name, scope }) => [
      {
        path: frontendURL(`accounts/:accountId/${slug}/conversations`),
        name,
        meta: { permissions: CONVERSATION_PERMISSIONS },
        component: ConversationView,
        props: () => ({ ...scope, queueSlug: slug }),
      },
      {
        path: frontendURL(
          `accounts/:accountId/${slug}/conversations/:conversationId`
        ),
        name: `${name}_conversation`,
        meta: { permissions: CONVERSATION_PERMISSIONS },
        component: ConversationView,
        props: route => ({
          ...scope,
          queueSlug: slug,
          conversationId: route.params.conversationId,
        }),
      },
    ]),
    // Retired queue paths. "My chats" was renamed "For you" (same queue, new
    // slug), Queued folded into Unassigned, and Archive went away (a shelved
    // conversation is no longer a place the product has). Bookmarks survive:
    ...[
      { slug: 'mine', list: 'conversation_mine' },
      { slug: 'queued', list: 'conversation_unassigned' },
      { slug: 'archive', list: 'home' },
    ].flatMap(({ slug, list }) => [
      {
        path: frontendURL(`accounts/:accountId/${slug}/conversations`),
        redirect: to => ({
          name: list,
          params: { accountId: to.params.accountId },
        }),
      },
      {
        path: frontendURL(
          `accounts/:accountId/${slug}/conversations/:conversationId`
        ),
        redirect: to =>
          list === 'home'
            ? {
                name: 'inbox_conversation',
                params: {
                  accountId: to.params.accountId,
                  conversation_id: to.params.conversationId,
                },
              }
            : {
                name: `${list}_conversation`,
                params: {
                  accountId: to.params.accountId,
                  conversationId: to.params.conversationId,
                },
              },
      },
    ]),
    {
      path: frontendURL('accounts/:accountId/mentions/conversations'),
      name: 'conversation_mentions',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: () => ({ conversationType: 'mention' }),
    },
    {
      path: frontendURL(
        'accounts/:accountId/mentions/conversations/:conversationId'
      ),
      name: 'conversation_through_mentions',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: route => ({
        conversationId: route.params.conversationId,
        conversationType: 'mention',
      }),
    },
    {
      path: frontendURL('accounts/:accountId/unattended/conversations'),
      name: 'conversation_unattended',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: () => ({ conversationType: 'unattended' }),
    },
    {
      path: frontendURL(
        'accounts/:accountId/unattended/conversations/:conversationId'
      ),
      name: 'conversation_through_unattended',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: route => ({
        conversationId: route.params.conversationId,
        conversationType: 'unattended',
      }),
    },
    {
      path: frontendURL('accounts/:accountId/ai-handling/conversations'),
      name: 'conversation_ai_handling',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: () => ({ conversationType: 'ai_handling' }),
    },
    {
      path: frontendURL(
        'accounts/:accountId/ai-handling/conversations/:conversationId'
      ),
      name: 'conversation_through_ai_handling',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: route => ({
        conversationId: route.params.conversationId,
        conversationType: 'ai_handling',
      }),
    },
    {
      path: frontendURL('accounts/:accountId/ai-resolved/conversations'),
      name: 'conversation_resolved_by_ai',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: () => ({ conversationType: 'resolved_by_ai' }),
    },
    {
      path: frontendURL(
        'accounts/:accountId/ai-resolved/conversations/:conversationId'
      ),
      name: 'conversation_through_resolved_by_ai',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: route => ({
        conversationId: route.params.conversationId,
        conversationType: 'resolved_by_ai',
      }),
    },
    {
      path: frontendURL('accounts/:accountId/participating/conversations'),
      name: 'conversation_participating',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: () => ({ conversationType: 'participating' }),
    },
    {
      path: frontendURL(
        'accounts/:accountId/participating/conversations/:conversationId'
      ),
      name: 'conversation_through_participating',
      meta: {
        permissions: CONVERSATION_PERMISSIONS,
      },
      component: ConversationView,
      props: route => ({
        conversationId: route.params.conversationId,
        conversationType: 'participating',
      }),
    },
  ],
};
